import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { gamoraApi } from '../services/api';

interface WorkflowShareDialogProps {
  open: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName?: string;
}

const WorkflowShareDialog: React.FC<WorkflowShareDialogProps> = ({
  open,
  onClose,
  workflowId,
  workflowName,
}) => {
  const [shareUrl, setShareUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerateLink = async () => {
    if (!workflowId) {
      setError('Workflow ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await gamoraApi.generateWorkflowShareLink(workflowId);
      setShareUrl(result.share_url);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy link to clipboard');
    }
  };

  const handleRevokeLink = async () => {
    if (!workflowId) return;

    setLoading(true);
    setError(null);

    try {
      await gamoraApi.revokeWorkflowShareLink(workflowId);
      setShareUrl('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to revoke share link');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShareUrl('');
    setError(null);
    setCopied(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShareIcon sx={{ color: '#1976d2' }} />
            <Typography variant="h6">Share Workflow</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {workflowName && (
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              Workflow: <strong>{workflowName}</strong>
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Share Link"
            value={shareUrl}
            InputProps={{
              readOnly: true,
              startAdornment: <LinkIcon sx={{ color: '#666', mr: 1 }} />,
              endAdornment: shareUrl && (
                <IconButton onClick={handleCopyLink} size="small" sx={{ ml: 1 }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleGenerateLink} variant="contained" disabled={loading}>
            {loading ? 'Generating...' : shareUrl ? 'Regenerate Link' : 'Generate Link'}
          </Button>
          {shareUrl && (
            <Button onClick={handleRevokeLink} variant="outlined" color="error" disabled={loading}>
              Revoke Link
            </Button>
          )}
          <Button onClick={handleClose} variant="text">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Link copied to clipboard"
      />
    </>
  );
};

export default WorkflowShareDialog;
